import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Menu, X, Phone, Smartphone, Mail } from "lucide-react";
import { FreemanJonesLogo } from "./FreemanJonesLogo";
import { ExternalLinkIcon } from "./primitives";
import { businessDetails, navItems } from "../data/content";
import { track } from "../lib/analytics";

const ext = {
  target: "_blank",
  rel: "noopener noreferrer",
};

export const UtilityCustomerBar = () => (
  <div className="bg-brand-navy text-white" data-testid="utility-bar">
    <div className="mx-auto flex max-w-content flex-col items-stretch gap-2 px-4 py-2 text-sm sm:flex-row sm:items-center sm:justify-between lg:px-8">
      <p className="font-semibold sm:text-left">Already a Freeman Jones customer?</p>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <a
          {...ext}
          href={businessDetails.customerAppUrl}
          onClick={() => track("customer_app_clicked", { location: "utility-bar" })}
          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full bg-brand-teal px-4 font-semibold text-white transition-colors hover:bg-brand-tealHover sm:min-h-0 sm:py-1.5"
          data-testid="utility-open-app"
          aria-label="Open Customer App — opens external customer service in a new tab"
        >
          <Smartphone className="h-4 w-4" aria-hidden="true" /> Open Customer App
          <ExternalLinkIcon />
        </a>
        <a
          {...ext}
          href={businessDetails.digitalPostboxUrl}
          onClick={() => track("digital_postbox_clicked", { location: "utility-bar" })}
          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full border border-white/40 px-4 font-semibold text-white transition-colors hover:bg-white/10 sm:min-h-0 sm:py-1.5"
          data-testid="utility-open-postbox"
          aria-label="Open Digital Postbox — opens external customer service in a new tab"
        >
          <Mail className="h-4 w-4" aria-hidden="true" /> Open Digital Postbox
          <ExternalLinkIcon />
        </a>
      </div>
    </div>
  </div>
);

export const MainHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef(null);
  const { pathname } = useLocation();
  // Section anchors need a "/" prefix when viewing a standalone legal page.
  const home = pathname === "/" ? "" : "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [menuOpen]);

  return (
    <>
      <header
        className={`sticky top-0 z-40 border-b transition-all duration-300 ${
          scrolled
            ? "border-brand-border bg-white/90 shadow-sm backdrop-blur-xl"
            : "border-transparent bg-brand-bg"
        }`}
        data-testid="main-header"
      >
        <div className="mx-auto flex max-w-content items-center justify-between gap-8 px-4 py-3 lg:px-8">
          <a href={`${home}#top`} aria-label="Freeman Jones — back to top" data-testid="logo-link" className="mr-2 shrink-0">
            <FreemanJonesLogo />
          </a>

          <nav aria-label="Primary" className="ml-auto hidden lg:block">
            <ul className="flex items-center gap-1 xl:gap-3">
              {navItems.map((item) => (
                <li key={item.href}>
                  <a
                    href={`${home}${item.href}`}
                    className="inline-flex min-h-[44px] items-center whitespace-nowrap rounded-full px-4 text-[15px] font-semibold text-brand-navy/80 transition-colors hover:bg-brand-teal/10 hover:text-brand-teal"
                    data-testid={`nav-${item.href.replace("#", "")}`}
                  >
                    {item.short}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <button
            type="button"
            ref={menuButtonRef}
            onClick={() => setMenuOpen(true)}
            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-brand-navy lg:hidden"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            data-testid="mobile-menu-open"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </header>

      {/* Rendered outside <header>: its backdrop-blur would otherwise trap this fixed overlay */}
      <MobileNavigation open={menuOpen} onClose={() => setMenuOpen(false)} openButtonRef={menuButtonRef} />
    </>
  );
};

export const MobileNavigation = ({ open, onClose, openButtonRef }) => {
  const dialogRef = useRef(null);
  const wasOpenRef = useRef(false);
  const { pathname } = useLocation();
  const home = pathname === "/" ? "" : "/";

  // Move focus into the menu when it opens; return it to the menu button on close.
  useEffect(() => {
    if (!open) {
      if (wasOpenRef.current) {
        wasOpenRef.current = false;
        openButtonRef?.current?.focus();
      }
      return;
    }
    wasOpenRef.current = true;
    // Delay so the container's visibility transition has started (a focus() call on a
    // still-visibility:hidden element is a no-op).
    const timer = window.setTimeout(() => {
      dialogRef.current?.querySelector('[data-testid="mobile-menu-close"]')?.focus();
    }, 100);
    return () => window.clearTimeout(timer);
  }, [open, openButtonRef]);

  // Close on Escape, wherever focus happens to be.
  useEffect(() => {
    if (!open) return;
    const onEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onEscape);
    return () => document.removeEventListener("keydown", onEscape);
  }, [open, onClose]);

  // Keep Tab / Shift+Tab cycling inside the menu while it's open.
  const trapFocus = (e) => {
    if (e.key !== "Tab" || !dialogRef.current) return;
    const focusables = dialogRef.current.querySelectorAll("a[href], button:not([disabled])");
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      ref={dialogRef}
      className={`fixed inset-0 z-[60] transition-[visibility] duration-300 lg:hidden ${
        open ? "visible" : "invisible"
      }`}
      role={open ? "dialog" : undefined}
      aria-modal={open ? true : undefined}
      aria-label="Menu"
      aria-hidden={!open}
      onKeyDown={open ? trapFocus : undefined}
    >
      <div
        className={`absolute inset-0 bg-brand-navy/70 transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
      />
      <div
        className={`absolute right-0 top-0 flex h-full w-[86%] max-w-sm flex-col bg-white p-5 shadow-xl transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between">
          <FreemanJonesLogo />
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-brand-navy"
            aria-label="Close menu"
            data-testid="mobile-menu-close"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Customer tools kept at the very top of the menu — never hidden */}
        <div className="mt-5 grid gap-2">
          <a
            {...ext}
            href={businessDetails.customerAppUrl}
            onClick={() => { track("customer_app_clicked", { location: "mobile-menu" }); onClose(); }}
            className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-brand-teal px-4 font-semibold text-white"
            data-testid="mobile-open-app"
            aria-label="Open Customer App — opens external customer service in a new tab"
          >
            Open Customer App <ExternalLinkIcon />
          </a>
          <a
            {...ext}
            href={businessDetails.digitalPostboxUrl}
            onClick={() => { track("digital_postbox_clicked", { location: "mobile-menu" }); onClose(); }}
            className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full border border-brand-navy px-4 font-semibold text-brand-navy"
            data-testid="mobile-open-postbox"
            aria-label="Open Digital Postbox — opens external customer service in a new tab"
          >
            Open Digital Postbox <ExternalLinkIcon />
          </a>
        </div>

        <nav aria-label="Mobile" className="mt-6">
          <ul className="flex flex-col">
            {navItems.map((item) => (
              <li key={item.href}>
                <a
                  href={`${home}${item.href}`}
                  onClick={onClose}
                  className="flex min-h-[48px] items-center border-b border-brand-border text-base font-semibold text-brand-navy"
                  data-testid={`mobile-nav-${item.href.replace("#", "")}`}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <a
          href={businessDetails.phoneLink}
          onClick={() => { track("phone_number_clicked", { location: "mobile-menu" }); onClose(); }}
          className="mt-auto inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-brand-navy px-4 font-semibold text-white"
          data-testid="mobile-call"
        >
          <Phone className="h-4 w-4" aria-hidden="true" /> Call {businessDetails.phoneDisplay}
        </a>
      </div>
    </div>
  );
};
